/**
 * Labor: ein sichtbarer Fehler (role=alert) schreibt das Fehlerpaket.
 * Der Alert-Text selbst wird nicht gelesen — der kann Akte oder Chat enthalten.
 */
import { isLaborPortalHostname } from '../../constants';
import { submitPortalVisibleError } from './portalCustomerFeedback';
import {
  portalLaufzeitZumAusgang,
  readDistFingerprint,
  readPortalDiagContext,
  sanitizeConsoleLines,
  setPortalFehlerausgangWriter,
} from './portalFehlerpaket';

const BUFFER_MAX = 8;
const consoleBuffer: string[] = [];
let consoleHooked = false;

function pushConsoleLine(line: string): void {
  const safe = sanitizeConsoleLines([line]);
  if (!safe[0]) return;
  consoleBuffer.push(safe[0]);
  if (consoleBuffer.length > BUFFER_MAX) consoleBuffer.shift();
}

function formatConsoleArg(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  return '';
}

export function installPortalFehlerpaketConsoleBuffer(): void {
  if (consoleHooked || typeof window === 'undefined') return;
  consoleHooked = true;
  for (const level of ['error', 'warn'] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      try {
        const line = args.map(formatConsoleArg).filter(Boolean).join(' ');
        if (line) pushConsoleLine(line);
      } catch {
        /* Puffer darf die Konsole nicht stören */
      }
      original(...args);
    };
  }
}

export function readPortalFehlerpaketConsoleBuffer(): string[] {
  return consoleBuffer.slice();
}

function browserDistFingerprint(): string {
  const build = typeof __PORTAL_BUILD__ === 'string' ? __PORTAL_BUILD__ : '';
  const srcs: string[] = [];
  if (typeof document !== 'undefined') {
    document.querySelectorAll('script[src]').forEach((node) => {
      const src = node.getAttribute('src');
      if (src) srcs.push(src);
    });
  }
  return readDistFingerprint({ portalBuild: build, scriptSrcs: srcs });
}

export function watchVisiblePortalErrors(opts: {
  customerId: string;
  onDiagCode?: (code: string) => void;
}): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  if (!opts.customerId.trim()) return () => {};
  if (!isLaborPortalHostname(window.location.hostname)) return () => {};
  installPortalFehlerpaketConsoleBuffer();
  const onWindowError = (event: Event) => {
    const fault = event as ErrorEvent;
    portalLaufzeitZumAusgang('error', fault.error ?? fault.message);
  };
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    portalLaufzeitZumAusgang('unhandledrejection', event.reason);
  };
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  setPortalFehlerausgangWriter((payload) => {
    const dist = payload.diag.dist_fingerprint || browserDistFingerprint();
    const qualityGate = readPortalDiagContext().qualityGate;
    void submitPortalVisibleError({
      customerId: opts.customerId.trim(),
      ...(payload.request_id ? { requestId: payload.request_id } : {}),
      ...(payload.diag.console_lines ? { consoleLines: payload.diag.console_lines } : {}),
      ...(payload.diag.last_diag_step ? { lastDiagStep: payload.diag.last_diag_step } : {}),
      ...(qualityGate ? { qualityGate } : {}),
      ...(dist ? { portalBuild: dist } : {}),
    })
      .then((result) => {
        if (result.diagCode) opts.onDiagCode?.(result.diagCode);
      })
      .catch(() => {
        /* Sichtbarer Fehler bleibt sichtbar. Kein zweiter Sammler. */
      });
  });

  const seen = new WeakSet<Element>();
  let timer = 0;
  let busy = false;
  let lastKey = '';

  const send = async () => {
    if (busy) return;
    const ctx = readPortalDiagContext();
    const lines = readPortalFehlerpaketConsoleBuffer();
    const dist = browserDistFingerprint();
    const key = [ctx.requestId, ctx.lastDiagStep, dist, lines.join('|')].join('~');
    if (key === lastKey) return;
    lastKey = key;
    busy = true;
    try {
      const result = await submitPortalVisibleError({
        customerId: opts.customerId.trim(),
        requestId: ctx.requestId || undefined,
        consoleLines: lines,
        lastDiagStep: ctx.lastDiagStep || undefined,
        qualityGate: ctx.qualityGate || undefined,
        pipeline: ctx.lastDiagStep ? [{ step: ctx.lastDiagStep }] : undefined,
        portalBuild: dist || undefined,
      });
      if (result.diagCode) opts.onDiagCode?.(result.diagCode);
    } catch {
      /* Sichtbarer Fehler bleibt sichtbar. Kein zweiter Alert, kein Auto-Fix. */
    } finally {
      busy = false;
    }
  };

  const scan = () => {
    let fresh = false;
    document.querySelectorAll('[role="alert"]').forEach((node) => {
      if (!(node instanceof Element) || seen.has(node) || !node.isConnected) return;
      const rect = node.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      seen.add(node);
      fresh = true;
    });
    if (!fresh) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      void send();
    }, 1200);
  };

  const observer = new MutationObserver(scan);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['role'],
  });
  scan();
  return () => {
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    setPortalFehlerausgangWriter(null);
    observer.disconnect();
    window.clearTimeout(timer);
  };
}

